import React, { useState, useEffect } from 'react';
import { Carousel } from 'react-responsive-carousel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretLeft, faCaretRight, faMagnifyingGlass, faTimes } from '@fortawesome/free-solid-svg-icons';
import { BasicFetch } from '../../utils/BasicFetch.ts';
import { Button, FormControl } from 'react-bootstrap';
import { SuggestReptileModal } from './SuggestReptileModal.tsx';


interface Reptile {
  id: number;
  reptile_name: string;
  reptile: string;
}

const hasSelectableSubReptiles = (reptileGroup: ReptileGroup) => {
  if (reptileGroup.reptiles.length > 0) return true;
  if (reptileGroup.subReptileGroups.length === 0) return false;
  return reptileGroup.subReptileGroups.some((subReptileGroup) => {
    return hasSelectableSubReptiles(subReptileGroup);
  })
}

export interface ReptileGroup {
  label: string;
  acronym?: string;
  id: number;
  reptiles: Reptile[];
  subReptileGroups: ReptileGroup[];
}

const MenuItem = ({ menuItem, selectFunction, value }: { menuItem: ReptileGroup, selectFunction: (reptileAccronym: string, reptile: Reptile | null) => void, value: string }) => {
  if (menuItem.reptiles.length === 0 && menuItem.subReptileGroups.length === 0 && !menuItem.acronym) return null;

  return (
    <div style={{ cursor: 'pointer' }} className={`d-flex flex-row w-100 pr-2 pl-3 justify-content-between reptile-select-item ${menuItem.acronym === value ? 'active' : ''}`} onClick={() => {
      if (menuItem.subReptileGroups.length + menuItem.reptiles.length === 0) {
        selectFunction(menuItem.acronym || '', menuItem);
        return;
      }
      selectFunction('', null);
    }}>
      <h5 className='text-left my-1'>
        {menuItem.label}
      </h5>
      {menuItem.subReptileGroups.length + menuItem.reptiles.length > 0 && <FontAwesomeIcon className='my-auto' icon={faCaretRight} />}
    </div>
  )
}

const ReptileItem = ({ reptile, selectFunction, value }: { reptile: Reptile, selectFunction: (reptileAccronym: string, reptile: Reptile) => void, value: string }) => {
  return (
    <div style={{ cursor: 'pointer' }} className={`d-flex flex-row w-100 pr-2 pl-3 justify-content-between reptile-select-item ${reptile.reptile === value ? 'active' : ''}`} onClick={() => {
      selectFunction(reptile.reptile, reptile);
    }}>
      <h5 className='my-1'>
        {reptile.reptile_name}
      </h5>
    </div>
  )
}

export const ReptileSelector: React.FC = () => {
  const [currentReptileGroups, setCurrentReptileGroups] = useState<ReptileGroup[][] | null>(null);
  const [usedReptiles, setUsedReptiles] = useState<Reptile[] | null>(null);
  const [value, setInitialValue] = useState<string>('');
  const [title, setTitle] = useState('');
  const [previousTitle, setPreviousTitle] = useState<string[]>([]);
  const [selectFunction, setSelectFunction] = useState<(reptile: string, reptileFullName: Reptile) => void>(() => { });
  const [show, setShow] = useState(false);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Reptile[]>([]);
  const [allReptiles, setAllReptiles] = useState<Reptile[]>([]);
  const [showSuggestReptile, setShowSuggestReptile] = useState(false);
  const closeModal = () => {
    setSearch('');
    setShow(false);
  }


  useEffect(() => {
    (window as any).setUpReptileSelect = (title: string, selectFunction: (reptile: string) => void, value) => {
      setTitle(title);
      setSelectFunction(selectFunction);
      setInitialValue(value);
      setShow(true);
    }
    (window as any).refetchReptileSelect = () => {
      getReptiles();
    }
    (window as any).closeSelector = () => {
      setShow(false);
    }
  }, [selectFunction, value, title, show])

  const getReptiles = () => {
    BasicFetch({
      url: `${(window as any).serverLocation}reptile_group/get_top`,
      method: 'GET',
      onSuccess: ({ reptile_groups, used_reptiles, all_reptiles }: { reptile_groups: ReptileGroup[], used_reptiles: Reptile[], all_reptiles: Reptile[] }) => {
        if (!reptile_groups) return;
        const filteredReptileGroups = reptile_groups.filter((reptileGroup) => {
          return hasSelectableSubReptiles(reptileGroup) || reptileGroup.acronym;
        });
        setCurrentReptileGroups([filteredReptileGroups]);
        setUsedReptiles(used_reptiles);
        setAllReptiles(all_reptiles);
      },
    })
  }

  useEffect(() => {
    setSearch('');
    getReptiles();
  }, []);


  useEffect(() => {
    if (!search) {
      setSearchResults([]);
      return;
    }

    const results = allReptiles?.filter((reptile) => {
      return reptile.reptile_name.toLowerCase().includes(search.toLowerCase());
    });
    setSearchResults(results || []);
  }, [search])

  if (!currentReptileGroups) return null;
  return (
    <>
      {showSuggestReptile && <SuggestReptileModal
        currentReptiles={allReptiles}
        prefill={search}
        onSuccess={() => setShowSuggestReptile(false)}
        closeModal={() => setShowSuggestReptile(false)}
      />}
      <div id='reptile-selector-shadow' style={{
        display: show ? 'block' : 'none',
      }} className='shadow fadeIn animated' onClick={(event) => {
        // if anything but the calendar is clicked, close the calendar
        if (event.target?.id === "reptile-selector-shadow") {
          closeModal();
        }
      }}>
        <div id="reptile-selector-popup" className='d-flex flex-column fadeIn animated rounded'>
          <div className='d-flex flex-row justify-content-between border-bottom pb-2'>
            {currentReptileGroups.length > 1 && <FontAwesomeIcon
              className='my-auto mr-3'
              size='xl'
              icon={faCaretLeft}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setCurrentReptileGroups(currentReptileGroups.slice(0, currentReptileGroups.length - 1));
                setTitle(previousTitle[previousTitle.length - 1]);
                setPreviousTitle(previousTitle.slice(0, previousTitle.length - 1));
              }}
            />}
            {title && <h4 className='flex-fill'>{title}</h4>}
            <div onClick={() => closeModal()} className='px-2 ml-auto' style={{ cursor: 'pointer' }} ><FontAwesomeIcon size='xl' icon={faTimes} /></div>
          </div>
          <div>
            <div className='d-flex flex-row my-3'>
              <FormControl
                type='text'
                placeholder='Search'
                className='bg-black text-light rounded'
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ boxShadow: 'none', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
              />
              <FontAwesomeIcon className='my-auto p-2 text-light' icon={faMagnifyingGlass} />
            </div>

            {search && searchResults.length > 0 && <div className='d-flex flex-column'>
              {searchResults.map((reptileGroup) => {
                return (
                  <ReptileItem key={reptileGroup.reptile} reptile={reptileGroup} value={value} selectFunction={(reptile, reptileGroup) => {
                    selectFunction(reptile, reptileGroup);
                    closeModal();
                  }} />
                )
              })}
            </div>}
            {search && searchResults.length === 0 && <div className='text-center text-light'>
              No results found, can't find what you're looking for?
              <div>
                <Button size='sm' className='mt-2' onClick={() => {
                  setShowSuggestReptile(true);
                }} variant='outline-light'>Suggest a reptile</Button>
              </div>
            </div>}


            {!search && <Carousel selectedItem={currentReptileGroups.length - 1} showArrows={false} showThumbs={false} swipeable={false} showStatus={false} showIndicators={false} infiniteLoop={true} autoPlay={false}>
              {currentReptileGroups.map((menuItemList, depth) => {
                return (
                  <>
                    {
                      menuItemList.map((menuItem, index) => {
                        return (
                          <MenuItem key={`${menuItem.label}-${index}-${depth}`} menuItem={menuItem} value={value} selectFunction={() => {
                            if (menuItem.subReptileGroups.length + menuItem.reptiles.length === 0) {
                              setCurrentReptileGroups([currentReptileGroups[0]]);
                              selectFunction(menuItem.acronym || '', menuItem);
                              setTitle('');
                              closeModal();
                              return;
                            }
                            setPreviousTitle([...previousTitle, title]);
                            setTitle(menuItem.label);
                            setCurrentReptileGroups([...currentReptileGroups, [...menuItem.subReptileGroups, ...menuItem.reptiles.map((reptile) => {
                              return {
                                label: reptile.reptile_name,
                                acronym: reptile.reptile,
                                id: reptile.id,
                                reptiles: [],
                                subReptileGroups: [],
                              }
                            })]]);
                          }} />
                        )
                      })
                    }
                  </>
                )
              })}
            </Carousel>}

            <hr />
            {
              usedReptiles && usedReptiles.length > 0 && <div className='d-flex flex-column'>
                <h5>Owned</h5>
                {usedReptiles.map((reptile) => {
                  return (
                    <ReptileItem key={reptile.reptile} reptile={reptile} value={value} selectFunction={(reptileAccronym, reptile) => {
                      selectFunction(reptileAccronym, reptile);
                      closeModal();
                    }} />
                  )
                })}
              </div>
            }

          </div>
        </div>
      </div>
    </>
  )
};

export default ReptileSelector;